import {
	BeckyModel,
	BeckyRequestPayload,
	getCompsOfFirstLevelContainersInFold,
	deStringifyExperiments,
	fixBuilderComponentType,
	getMergedDataWithFlatPageStructure,
	getCompsInFirstFold,
	HEURISTIC_FIRST_FOLD_HEIGHT_DESKTOP,
	HEURISTIC_FIRST_FOLD_HEIGHT_MOBILE,
	geResponsiveCompsInFirstFold,
	getRequestedPageCompId,
	mergeWithExternalSkinsInfo,
	loadTranslations,
} from '@wix/thunderbolt-becky-root'
import { BeckyTopology, FetchFn, Logger, MetricsReporter } from '@wix/thunderbolt-becky-types'
import { mergeMappers } from '@wix/thunderbolt-components'
import type { BeckyExperiments, Translations } from '@wix/thunderbolt-commons'
import { getComponentMappers, getComponentSkinParams } from './componentsSiteAssets'
import { applyAnywhereThemeOverride } from './themeOverrides'

export type ResolveBeckyModelParams = {
	payload: BeckyRequestPayload
	fetchFn: FetchFn
	beckyTopology: BeckyTopology
	shapeStore?: Record<string, string>
	builderPresetsStore?: BeckyModel['builderPresetsStore']
	metricsReporter: MetricsReporter
	logger: Logger
}

export const resolveBeckyModel = async ({
	payload,
	beckyTopology: { publicTopology, environmentTopology },
	fetchFn,
	shapeStore = {},
	builderPresetsStore = {},
	metricsReporter,
	logger,
}: ResolveBeckyModelParams): Promise<BeckyModel> => {
	const {
		pages,
		viewMode = 'desktop',
		beckyExperiments,
		isWixCodeOnPage: isWixCodeOnPageParam,
		isWixCodeOnSite: isWixCodeOnSiteParam,
		isClientSdkOnSite: isClientSdkOnSiteParam,
		hasTPAWorkerOnSite: hasTPAWorkerOnSiteParam,
		freemiumBanner: freemiumBannerParam,
		coBrandingBanner: coBrandingBannerParam,
		dayfulBanner: dayfulBannerParam,
		mobileActionsMenu: mobileActionsMenuParam,
		shouldSplitByPresets,
		externalBaseUrl,
		metaSiteId,
		siteId,
		clientSpecMap,
		language,
		originalLanguage,
		isWixSite,
		languageResolutionMethod,
		isMultilingualEnabled,
		useSandboxInHTMLComp,
		routersInfo,
		deviceType,
		qaMode,
		testMode,
		debugMode,
		isInSeo: isInSeoParam,
		isPremiumDomain,
		migratingToOoiWidgetIds,
		stylableItemsCss,
		stylableInstance,
		registryLibrariesTopology,
		isHttps,
		anywhereThemeOverride,
		seoTranslations,
		formFactor,
		disableStaticPagesUrlHierarchy,
		aboveTheFoldSectionsNum,
		isTrackClicksAnalyticsEnabled,
		editorName,
		isSocialElementsBlocked,
		builderWidgetsIds,
		cssPerBreakpointWidgetIds,
		appDefinitionIdsWithCustomCss: appDefinitionIdsWithCustomCssRaw,
	} = payload

	const isResponsive = payload.isResponsive === 'true'
	const experiments: BeckyExperiments = deStringifyExperiments(beckyExperiments)
	const shouldRenderWixAds = freemiumBannerParam === 'true'
	const shouldShowCoBrandingBanner = coBrandingBannerParam === 'true'
	const shouldShowDayfulBanner = dayfulBannerParam === 'true'
	const appDefinitionIdsWithCustomCss = appDefinitionIdsWithCustomCssRaw
		? JSON.parse(appDefinitionIdsWithCustomCssRaw)
		: []

	const [processedPages, compMappers, externalSkinParams] = await Promise.all([
		metricsReporter.runAsyncAndReport(
			() =>
				getMergedDataWithFlatPageStructure({
					pages,
					viewMode,
					isResponsive,
					experiments,
					shouldRenderFreemiumBanner: shouldRenderWixAds,
					shouldShowCoBrandingBanner,
					shouldShowDayfulBanner,
					editorName: experiments['specs.thunderbolt.forceStudio'] ? 'Studio' : editorName,
				}),
			'getMergedDataWithFlatPageStructure'
		),
		metricsReporter.runAsyncAndReport(
			() =>
				getComponentMappers({
					fetchFn,
					registryLibrariesTopology,
					metricsReporter,
					isExperimentOpen: (specName: string) => experiments.hasOwnProperty(specName),
					isPlatformBuilderModule: payload.module?.startsWith('builder-component'),
					isEditor: payload.isEditor === 'true',
				}),
			'getComponentMappers'
		),
		metricsReporter.runAsyncAndReport(
			() =>
				getComponentSkinParams({
					fetchFn,
					registryLibrariesTopology,
					metricsReporter,
					isExperimentOpen: (specName: string) => experiments.hasOwnProperty(specName),
					isPlatformBuilderModule: payload.module?.startsWith('builder-component'),
					isEditor: payload.isEditor === 'true',
				}),
			'getComponentSkinParams'
		),
	])

	const componentsSkinParams = mergeWithExternalSkinsInfo(externalSkinParams)

	const data = anywhereThemeOverride
		? applyAnywhereThemeOverride({ data: processedPages.data, anywhereThemeOverride })
		: processedPages.data

	const isInSeo = isInSeoParam === 'true'

	const siteStructure = { ...processedPages.structure, ...processedPages.masterPageStructure }
	const isOneDocMigrated = !!siteStructure.masterPage.layoutQuery

	const compsInFoldThreshold =
		!viewMode || viewMode === 'desktop' ? HEURISTIC_FIRST_FOLD_HEIGHT_DESKTOP : HEURISTIC_FIRST_FOLD_HEIGHT_MOBILE
	const pageCompId = getRequestedPageCompId(pages)
	const compsInFoldMap = isResponsive
		? metricsReporter.runAndReport(
				() =>
					geResponsiveCompsInFirstFold(
						processedPages,
						pageCompId,
						processedPages.data.layout_data,
						processedPages.data.variants_data,
						aboveTheFoldSectionsNum
					),
				'geResponsiveCompsInFirstFold'
		  ) || {}
		: isOneDocMigrated
		? metricsReporter.runAndReport(
				() =>
					getCompsOfFirstLevelContainersInFold(
						siteStructure,
						processedPages.data.layout_data,
						viewMode === 'mobile',
						compsInFoldThreshold
					),
				'getCompsOfFirstLevelContainersInFold'
		  )
		: metricsReporter.runAndReport(
				() => getCompsInFirstFold(siteStructure, compsInFoldThreshold),
				'getCompsInFirstFold'
		  )
	if (experiments['specs.thunderbolt.BuilderComponent']) {
		const { structure, masterPageStructure } = fixBuilderComponentType({
			masterPageStructure: processedPages.masterPageStructure,
			structure: processedPages.structure,
			data: processedPages.data,
			clientSpecMap,
			builderWidgetsIds: builderWidgetsIds ? JSON.parse(builderWidgetsIds) : {},
		})
		processedPages.masterPageStructure = masterPageStructure
		processedPages.structure = structure
	}

	const globalTranslations: Translations = await loadTranslations(language!)

	const isExperimentOpen = (name: keyof BeckyModel['experiments']) => experiments.hasOwnProperty(name)

	const componentsMappers = metricsReporter.runAndReport(
		() => mergeMappers(compMappers, isExperimentOpen, logger),
		'mergeMappers'
	)

	return {
		...processedPages,
		data,
		seoTranslations,
		externalBaseUrl,
		metaSiteId,
		siteId,
		previousStore: new WeakMap(),
		viewMode: viewMode || 'desktop',
		environmentTopology,
		publicTopology,
		language: language!,
		isMultilingualEnabled: isMultilingualEnabled === 'true',
		disableStaticPagesUrlHierarchy: disableStaticPagesUrlHierarchy === 'true',
		languageResolutionMethod,
		originalLanguage,
		useSandboxInHTMLComp: useSandboxInHTMLComp === 'true',
		isResponsive,
		editorName,
		isWixSite: isWixSite === 'true',
		hasTPAWorkerOnSite: hasTPAWorkerOnSiteParam === 'true',
		fetchRequestsStore: {},
		afterFirstRecalculate: false,
		isWixCodeOnPage: isWixCodeOnPageParam === 'true',
		isWixCodeOnSite: isWixCodeOnSiteParam === 'true',
		isClientSdkOnSite: isClientSdkOnSiteParam === 'true',
		shouldSplitByPresets: shouldSplitByPresets === 'true',
		renderFlags: {
			isWixAdsAllowed: shouldRenderWixAds,
			isWixBlocks: false,
			showScreenInComp: isInSeo,
			componentViewMode: 'preview',
			componentPreviewStates: {},
			isBackToTopButtonAllowed: false,
			shouldBlockGoogleMapInteraction: false,
			shouldResetTinyMenuZIndex: false,
			shouldResetSlideShowNextPrevButtonsPosition: false,
			shouldResetSubscribeFormMinMaxWidth: false,
			shouldUpdateJsonFromMeasureMap: false,
			isPlayingAllowed: false,
			isSocialInteractionAllowed: false,
			isExternalNavigationAllowed: true,
			isSlideShowGalleryClickAllowed: true,
			renderFixedPositionContainers: true,
			shouldResetGalleryToOriginalState: false,
			shouldResetComponent: true,
			siteScale: 1,
			isComponentTransitionAllowed: true,
			allowShowingFixedComponents: true,
			isZoomAllowed: false,
			renderFixedPositionBackgrounds: false,
			extraSiteHeight: 0,
			ignoreComponentsHiddenProperty: [],
			ignoreComponentsCollapsedProperty: [],
			transparentComponentsOverlay: -1,
			showHiddenComponents: false,
			blockingLayer: null,
			allowSiteOverflow: true,
			renderScrollSnap: true,
			renderSticky: true,
			compsToRenderSticky: {},
			enablePageAutoGrowForDetachedComponents: true,
			shouldRenderTPAsIframe: true,
			isAdi: false,
			isLivePreviewOpenForEditor: true,
			isExternalComponentsLoading: false,
			forceDisableSiteOverflow: false,
		},
		meshRuntimeData: {
			absoluteComps: [],
		},
		shouldRenderWixAdsOnNavigation: shouldRenderWixAds,
		shouldShowCoBrandingBanner,
		shouldShowDayfulBanner,
		shouldShowMobileActionsMenu: mobileActionsMenuParam === 'true',
		isPremiumDomain: isPremiumDomain === 'true',
		clientSpecMap,
		componentsMappers,
		componentsSkinParams,
		experiments,
		routersInfo: routersInfo!,
		deviceType: deviceType!,
		qaMode: qaMode === 'true',
		testMode: testMode === 'true',
		debugMode: debugMode === 'true',
		isInSeo,
		migratingToOoiWidgetIds: migratingToOoiWidgetIds ? migratingToOoiWidgetIds.split(',') : [],
		stylableItemsCss: stylableItemsCss || {},
		stylableInstance,
		themeCounter: 0,
		enableVariantsTransitionsInEditor: true,
		compIdToIgnoredTransformProps: {},
		ignoredTransformPropsForType: {},
		isSocialElementsBlocked: isSocialElementsBlocked === 'true',
		globalIgnoredTransformProps: [],
		isHttps: isHttps === 'true',
		shapeStore,
		builderPresetsStore,
		compsInFoldMap,
		imageCompsTargetScale: 1,
		formFactor,
		anywhereThemeOverride,
		activeVariant: '',
		activeBreakpoints: {},
		globalTranslations,
		ooiCssConfigStore: {},
		ooiWidgetsAppSettings: {},
		isTrackClicksAnalyticsEnabled: isTrackClicksAnalyticsEnabled === 'true',
		isOneDocMigrated,
		cssPerBreakpointWidgetIds: cssPerBreakpointWidgetIds ? cssPerBreakpointWidgetIds.split(',') : [],
		requestedPageId: payload.pageId || payload.pageCompId || undefined,
		widgetIdToPageIdMap: payload.widgetIdToPageIdMap,
		appDefinitionIdsWithCustomCss,
	}
}
